import { forwardRef, useEffect, SetStateAction } from "react";
import { FieldError } from "react-hook-form";
import {
  Field,
  FormFieldStatus,
  Hint,
  Input,
  Label,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";

import { TrackingErrorOrigin } from "../consts";

import { getInputErrorStatus } from "src/utils/inputValidation";
import { FieldLevelError } from "src/common/api";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { TrackingEvent } from "src/common/trackingEvents";

interface EmailFieldProps {
  emailAddress?: string;
  error?: FieldError;
  apiError?: FieldLevelError;
  autoComplete?: boolean;
  setFocusTime?: React.Dispatch<SetStateAction<number>>;
}

export const EmailField = forwardRef<HTMLInputElement, EmailFieldProps>(
  (
    {
      emailAddress,
      error,
      apiError,
      setFocusTime,
      autoComplete = true,
      ...field
    },
    ref
  ) => {
    const { sendEvent } = useSegment();

    useEffect(() => {
      if (error?.type) {
        sendEvent(TrackingEvent.SIGNUP_EMAIL_FIELD_VALIDATION_FAILED, {
          label: error?.type,
          error_origin: TrackingErrorOrigin.client_side,
        });
      }
    }, [error?.type, sendEvent]);

    useEffect(() => {
      if (apiError?.message) {
        sendEvent(TrackingEvent.SIGNUP_EMAIL_FIELD_VALIDATION_FAILED, {
          label: apiError.message,
          error_origin: TrackingErrorOrigin.server_side,
        });
      }
    }, [apiError?.message, sendEvent]);

    return (
      <Field data-testid="email">
        <Label htmlFor="email">
          <Trans id="Email">Email</Trans>
        </Label>
        <Input
          {...field}
          ref={ref}
          type="email"
          name="email"
          id="email"
          defaultValue={emailAddress}
          status={getInputErrorStatus(!!error)}
          autoComplete={autoComplete ? "on" : "off"}
          onFocus={() => setFocusTime && setFocusTime(new Date().getTime())}
        />
        {error?.type === "pattern" && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="email.field-validation-warning">
              Please enter a valid email
            </Trans>
          </Hint>
        )}
        {error?.type === "required" && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="email.field-required-warning">Email is required</Trans>
          </Hint>
        )}
        {error?.type === "validate" && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="email.canonical-gocardless-warning">
              {`Cannot create account using canonical GoCardless email. Use '+' to separate name ie.: jblack+test@gocardless.com`}
            </Trans>
          </Hint>
        )}
        {apiError && (
          <Hint status={FormFieldStatus.Danger}>
            <Trans id="Email">Email</Trans> {" " + apiError.message}
          </Hint>
        )}
      </Field>
    );
  }
);
